/**
 * HOT board search cluster (SEO Master Document, section 7).
 *
 * These pages are editorial entry points for search intent. They never
 * duplicate the ranking engine: each page links straight to the real board
 * at /c/{board} and only previews the live top entries.
 */

export type HotTopic = {
  /** URL segment under /hot/ */
  slug: string;
  /** Real category board slug this intent maps to, when there is one. */
  board?: string;
  h1: string;
  title: string;
  description: string;
  keywords: string[];
  intro: string;
  sections: { h2: string; body: string[] }[];
  faq: [string, string][];
  related: string[];
};

const CTA_BOARD = "Every position on the board is live. Heat decides the order, the board decides who gets seen.";

export const HOT_TOPICS: HotTopic[] = [
  {
    slug: "discover",
    h1: "What is hot right now?",
    title: "What Is Hot Right Now — Discover What People Are Backing | BeHot.Now",
    description:
      "See what is hot right now on BeHot.Now: live boards for products, apps, startups, AI, creators, music, brands and events, ranked by Heat in real time.",
    keywords: [
      "what is hot right now",
      "discover new products",
      "discover creators",
      "live leaderboard",
      "attention marketplace",
    ],
    intro:
      "BeHot.Now is a discovery surface for anything that wants attention. Instead of an algorithm deciding what you see, people put Heat behind the listings they believe in, and the boards reorder in real time. This hub is the fastest way into every live board.",
    sections: [
      {
        h2: "How discovery works here",
        body: [
          "Every listing lives on a category board. The board sorts by Heat, so the things people are actively backing right now sit at the top. Nothing is hidden behind a personalisation model and nothing decays because a feed decided you were not interested.",
          "Because ranking is public and paid, the boards stay honest: you can always see the position, the Heat behind it and the click count on a listing.",
        ],
      },
      {
        h2: "What you can discover",
        body: [
          "Products, apps, startups, AI tools, consumer tech, creators, influencers, musicians, DJs, artists, bands, brands, businesses, events, gaming and social accounts all have their own live board.",
          "Each listing links out to the real destination — a site, a store, a release, a profile — so discovery ends in something you can actually use.",
        ],
      },
      {
        h2: "Getting listed yourself",
        body: [
          "Anyone can list. You pick a board, pay the minimum boost for that board with Heat from your wallet and your listing enters moderation. Once approved it competes like everything else.",
          CTA_BOARD,
        ],
      },
    ],
    faq: [
      ["What does 'hot' mean on BeHot.Now?", "Hot means the listing currently holds Heat on a live board. The more Heat behind a listing, the higher it ranks in its category."],
      ["Is discovery free?", "Browsing every board is free and open to everyone, logged in or not. You only pay when you want your own listing on a board."],
      ["How often do the boards change?", "Continuously. Every boost applies immediately, so positions can move within seconds."],
      ["Can I discover people, not just products?", "Yes. Creators, influencers, artists, DJs, musicians and social accounts all have dedicated boards with public profiles behind them."],
    ],
    related: ["howitworks", "pricing", "attention", "products", "creators"],
  },
  {
    slug: "howitworks",
    h1: "How do BeHot.Now HOT boards work?",
    title: "How BeHot.Now HOT Boards Work — Paid Leaderboard Explained | BeHot.Now",
    description:
      "A plain explanation of how BeHot.Now HOT boards rank listings: Heat, minimum boosts, board duration, moderation and how positions change in real time.",
    keywords: [
      "how paid leaderboard works",
      "how BeHot ranking works",
      "pay to rank explained",
      "heat ranking",
    ],
    intro:
      "A HOT board is a public, category-specific leaderboard. Listings are ordered by the Heat behind them. This page explains the whole mechanic end to end, with no marketing language.",
    sections: [
      {
        h2: "Heat is the ranking unit",
        body: [
          "Heat is the internal credit that powers everything on BeHot.Now. You top up a wallet, you spend Heat to list and to boost, and your position on a board reflects the total Heat behind your listing.",
          "There is no hidden quality score, no editorial promotion and no algorithmic reshuffle. The order you see is the order of Heat.",
        ],
      },
      {
        h2: "Listing, moderation and going live",
        body: [
          "You choose a board, pay that board's minimum boost from your wallet, and submit. The listing goes into moderation so the boards stay clean. If a moderator has not reviewed it within the automatic window, it is approved automatically and goes live.",
          "Every listing carries its destination link, description, category, position, Heat total and public click count.",
        ],
      },
      {
        h2: "Boosting and staying on top",
        body: [
          "Owners can add Heat to their own listing at any time to climb. Fans can also back a listing they like — anyone can add Heat to someone else's listing, which is what makes the boards feel like a public contest rather than an ad auction.",
          "Boosts run for the board's boost duration. When Heat expires, the listing drops, so the top of a board always reflects current interest, not history.",
        ],
      },
    ],
    faq: [
      ["Is rank only about how much you pay?", "Rank follows the Heat behind a listing. Heat comes from the owner's boosts and from anyone else who backs the listing."],
      ["What happens when my boost expires?", "Your listing loses that Heat and moves down the board. You can boost again at any time to climb back."],
      ["Who approves listings?", "Moderators review new listings and edits. If no review happens inside the automatic window, the listing is approved automatically."],
      ["Can other people boost my listing?", "Yes. Fan boosts are a core mechanic — supporters can push a listing they want to see at the top."],
    ],
    related: ["pricing", "discover", "attention"],
  },
  {
    slug: "pricing",
    h1: "How much does it cost to get hot?",
    title: "HOT Board Pricing — What It Costs to List and Boost | BeHot.Now",
    description:
      "What it costs to get on a BeHot.Now HOT board: minimum boosts per category, wallet top-ups, boost duration and how much you need to take the top spot.",
    keywords: [
      "BeHot pricing",
      "HOT board cost",
      "boost listing cost",
      "paid leaderboard pricing",
    ],
    intro:
      "There is no subscription and no monthly plan. You top up a wallet, and you spend from it when you list or boost. This page explains every cost you can hit.",
    sections: [
      {
        h2: "The cost of a listing",
        body: [
          "Listing is not free. Each board has a minimum boost that you pay when you submit, which is what puts your listing into the ranking rather than into a directory nobody reads.",
          "Minimums differ per board because demand differs per board. The exact amount for a category is always shown on that board before you pay.",
        ],
      },
      {
        h2: "The cost of climbing",
        body: [
          "To climb you add Heat. There is no fixed price for position one — it depends on how much Heat the current leader holds and how long their boost has left to run.",
          "Because boosts expire, holding the top spot is an ongoing decision rather than a one-time purchase.",
        ],
      },
      {
        h2: "Wallets and top-ups",
        body: [
          "All payments come from your BeHot.Now wallet, never directly from a card at checkout. You top up once and then everything on the platform — listings, boosts, Marketplace orders — draws from that balance.",
          "Reward Heat earned through referrals can be spent on boosts and Marketplace orders. Full pricing detail lives in the Terms of Service.",
        ],
      },
    ],
    faq: [
      ["Is there a subscription?", "No. You only pay for what you list and boost."],
      ["What is the minimum top-up?", "Top-ups start small and are billed in AED. The exact minimum and the current bonus are shown in the top-up panel."],
      ["Do I pay by card when I list?", "No. You pay from your wallet. If your balance is short, the flow takes you to top up and then finishes the listing automatically."],
      ["Can I get a refund on a boost?", "Boosts are consumed as they run. EU consumers can read the right of withdrawal page for their statutory rights."],
    ],
    related: ["howitworks", "discover", "attention"],
  },
  {
    slug: "attention",
    h1: "Compete for attention.",
    title: "The Attention Marketplace — Compete for Visibility | BeHot.Now",
    description:
      "Attention is the scarce resource. BeHot.Now turns it into an open market: public boards where anything can compete for visibility, backed by profiles, services and payouts.",
    keywords: [
      "attention marketplace",
      "compete for visibility",
      "buy attention",
      "get noticed online",
    ],
    intro:
      "Every platform sells attention. Most of them hide the auction. BeHot.Now puts the auction on the page: the boards are public, the Heat is public and the position is public.",
    sections: [
      {
        h2: "Why an open market beats an ad platform",
        body: [
          "On an ad platform you bid privately against strangers and hope the delivery matches the promise. On a HOT board you can see exactly what the position costs, who holds it and how many clicks the listing has earned.",
          "That makes visibility something you can plan rather than something you buy blind.",
        ],
      },
      {
        h2: "Attention is only the entry point",
        body: [
          "Ranking on its own is a scoreboard. What makes it useful is what sits behind it: a public profile, services people can buy, promotion campaigns, earnings and payouts, a referral program and funding for eligible projects.",
          "A listing that ranks can convert into a follow, a booking, an order or a payout instead of just a spike of traffic.",
        ],
      },
      {
        h2: "Who competes here",
        body: [
          "Founders launching products, indie apps, AI tools, musicians pushing a release, DJs and artists building a following, creators and influencers growing an audience, brands testing positioning, and local businesses fighting for a specific city.",
          CTA_BOARD,
        ],
      },
    ],
    faq: [
      ["Is this just paid advertising?", "It is paid visibility, but the pricing, the position and the performance are public, which conventional ad platforms do not do."],
      ["What can compete on a board?", "Anything that fits one of the categories: a product, an app, a startup, a release, a profile, an event, a brand or a business."],
      ["Do I need an audience to start?", "No. The board is the audience. That is the point of listing."],
      ["Can I connect a listing to what I sell?", "Yes. Listings can link to your public profile and to Marketplace services so attention turns into orders."],
    ],
    related: ["discover", "pricing", "howitworks", "brands"],
  },

  // ---- Real board entries -------------------------------------------------
  {
    slug: "products",
    board: "consumer-tech",
    h1: "HOT Products",
    title: "HOT Products — Live Product Leaderboard & Discovery | BeHot.Now",
    description:
      "The live BeHot.Now product leaderboard. See which products people are backing right now, discover new launches and put your own product on the board.",
    keywords: ["product leaderboard", "product discovery", "new products", "launch a product"],
    intro:
      "A product leaderboard that does not reset at midnight and does not depend on who has the biggest launch-day mailing list. Products rank by the Heat behind them, so a good product with real backers can hold the top spot for as long as the backing lasts.",
    sections: [
      {
        h2: "Why launch here",
        body: [
          "Launch platforms are a single day. A HOT board is continuous — you can list the week you ship, the month you raise or the day you finally get the pricing right.",
          "You keep the destination link, so traffic goes to your own site and your own analytics.",
        ],
      },
      {
        h2: "What makes a product rank",
        body: [
          "Heat from the owner plus Heat from supporters. If your users care enough to boost you, they can move you up the board without you spending more.",
          "Click counts are public, so buyers can see which products actually pull attention rather than which ones simply bought a slot.",
        ],
      },
    ],
    faq: [
      ["What counts as a product?", "Physical goods, consumer tech, hardware, digital products and paid tools all fit this board."],
      ["Is it better than a launch day?", "It is different. A launch day is a spike; a board position is sustained visibility you control."],
      ["Can customers boost my product?", "Yes. Anyone can add Heat to a listing they want to see higher."],
    ],
    related: ["apps", "startups", "ai", "brands"],
  },
  {
    slug: "apps",
    board: "apps",
    h1: "HOT Apps",
    title: "HOT Apps — Live App Leaderboard & App Discovery | BeHot.Now",
    description:
      "Discover the apps people are backing right now, or promote your own app on a live BeHot.Now leaderboard with public rankings and click counts.",
    keywords: ["app leaderboard", "promote app", "discover apps", "app discovery"],
    intro:
      "App stores rank by installs and reviews, which rewards apps that are already big. The HOT Apps board ranks by Heat, which gives a new app a straight route to the top of a page people actually browse.",
    sections: [
      {
        h2: "Discovery for indie apps",
        body: [
          "You do not need store optimisation tricks or a review farm. You need a listing, a clear description and Heat behind it.",
          "Your listing links directly to the store page or the web app, and the click count on the listing tells you whether the copy is working.",
        ],
      },
      {
        h2: "Sustained visibility, not a spike",
        body: [
          "Boosts run for the board duration and can be topped up whenever you have a release, an update or a campaign to support.",
          "Pair the board with a public profile so people who find the app can also find the person behind it.",
        ],
      },
    ],
    faq: [
      ["Mobile apps or web apps?", "Both. Any app with a public destination link can be listed."],
      ["Does the store ranking matter here?", "No. The board is independent of store rankings."],
      ["Can I list more than one app?", "Yes, each app is its own listing with its own position."],
    ],
    related: ["products", "startups", "ai", "gaming"],
  },
  {
    slug: "startups",
    board: "startups",
    h1: "HOT Startups",
    title: "HOT Startups — Live Startup Leaderboard & Discovery | BeHot.Now",
    description:
      "See which startups are getting attention right now on the live BeHot.Now board, or list your startup and compete for visibility with founders and operators.",
    keywords: ["startup leaderboard", "startup discovery", "promote startup", "get startup seen"],
    intro:
      "Getting a startup seen is the hardest unpaid job a founder has. The HOT Startups board gives you a public place to compete for that attention with a budget you set.",
    sections: [
      {
        h2: "A predictable alternative to launch roulette",
        body: [
          "You are not waiting for a newsletter, a subreddit or an algorithm to notice you. You list, you boost, you rank, and the position is visible to anyone browsing the board.",
          "Investors, operators and early adopters browsing for what is new see the same board you do.",
        ],
      },
      {
        h2: "Turn the traffic into something",
        body: [
          "Connect the listing to a public profile so visitors can see the team, the socials and anything you sell.",
          "Eligible projects can also connect the funding layer, so attention and capital sit on the same page.",
        ],
      },
    ],
    faq: [
      ["Pre-launch or post-launch?", "Both. A waitlist page is a perfectly valid destination."],
      ["Does listing affect fundraising?", "It gives you a public, dated signal of traction and attention. What you do with it is up to you."],
      ["How much do I need to start?", "The board's minimum boost, paid from your wallet. The amount is shown before you pay."],
    ],
    related: ["ai", "apps", "products", "business"],
  },
  {
    slug: "ai",
    board: "ai",
    h1: "HOT AI",
    title: "HOT AI — Live AI Tool Leaderboard & Rankings | BeHot.Now",
    description:
      "The live AI leaderboard on BeHot.Now. Discover the AI tools people are backing right now, or rank your own AI product on a public board.",
    keywords: ["AI leaderboard", "AI tools ranking", "discover AI tools", "promote AI tool"],
    intro:
      "There are more AI tools than anyone can evaluate. The HOT AI board is a filter with skin in the game: tools rank by the Heat people put behind them, not by how many directories they submitted to.",
    sections: [
      {
        h2: "A signal you can read",
        body: [
          "Position plus public click count tells you two different things: how much someone believes in the tool, and how many people actually clicked through. Directory listings give you neither.",
          "Because boosts expire, the board reflects current interest instead of a permanent list of everything ever launched.",
        ],
      },
      {
        h2: "For AI builders",
        body: [
          "List the tool, link it to your site, and boost when you ship something worth attention. Supporters can boost you too.",
          "If you sell AI services as well as a product, connect the Marketplace so people can hire you from the same profile.",
        ],
      },
    ],
    faq: [
      ["What kind of AI products fit?", "Tools, agents, models, wrappers, APIs and AI-first apps all fit."],
      ["Is there a review process?", "Yes, every listing goes through moderation before it goes live."],
      ["Can I rank an open-source project?", "Yes, as long as it has a public destination link."],
    ],
    related: ["apps", "startups", "products", "discover"],
  },
  {
    slug: "creators",
    board: "creators",
    h1: "HOT Creators",
    title: "HOT Creators — Live Creator Rankings & Discovery | BeHot.Now",
    description:
      "Discover the creators people are backing right now on BeHot.Now, or rank your own creator profile on a public board and turn attention into work.",
    keywords: ["creator ranking", "discover creators", "creator leaderboard", "promote creator profile"],
    intro:
      "Follower counts measure the past. The HOT Creators board measures now: who is being backed today, in public, by people willing to spend on it.",
    sections: [
      {
        h2: "Ranking as a creator",
        body: [
          "List your profile, link your main destination and let your audience boost you. Fan boosts mean your community can put you on top without you spending everything yourself.",
          "The listing links to your public BeHot.Now profile, which collects your socials, your work and anything you sell.",
        ],
      },
      {
        h2: "From attention to income",
        body: [
          "Creators can publish services in the Marketplace and take paid work directly from the same profile that ranks on the board.",
          "Earnings land in a separate Earn wallet that can be withdrawn to a bank account or a USDC/USDT wallet after verification.",
        ],
      },
    ],
    faq: [
      ["Do I need a big following?", "No. The board is how people find you in the first place."],
      ["Can my fans push me up?", "Yes. Any user can boost your listing, at any amount."],
      ["Can I get paid through BeHot.Now?", "Yes, through Marketplace orders and the Earn wallet."],
    ],
    related: ["influencers", "social", "artists", "music"],
  },
  {
    slug: "influencers",
    board: "influencers",
    h1: "HOT Influencers",
    title: "HOT Influencers — Live Influencer Rankings & Discovery | BeHot.Now",
    description:
      "Find influencers who are getting attention right now, or rank your own influencer profile on a public BeHot.Now board and take brand work directly.",
    keywords: ["influencer ranking", "discover influencers", "find influencers", "influencer leaderboard"],
    intro:
      "Brands looking for influencers usually pay a database for stale numbers. The HOT Influencers board is live, public and free to browse, and every profile on it is a real account you can contact.",
    sections: [
      {
        h2: "For brands",
        body: [
          "Browse the board, open the profile, check the connected socials and hire directly through the Marketplace if the creator offers services.",
          "No agency markup sits between you and the person doing the work.",
        ],
      },
      {
        h2: "For influencers",
        body: [
          "Ranking puts you in front of brands who are actively looking. Publishing services turns that visibility into inbound work with clear prices.",
          "Payouts go to your Earn wallet and out to your verified bank account or crypto wallet.",
        ],
      },
    ],
    faq: [
      ["Which platforms count?", "Instagram, TikTok, YouTube, X, Twitch and anywhere else you have an audience — you connect them on your profile."],
      ["Is there a fee on brand work?", "A platform fee applies to completed Marketplace orders. It is deducted before the balance reaches your Earn wallet."],
      ["Can brands boost me?", "Yes, anyone can boost a listing."],
    ],
    related: ["creators", "social", "brands", "business"],
  },
  {
    slug: "music",
    board: "music",
    h1: "HOT Music",
    title: "HOT Music — Live Music Chart & Discovery | BeHot.Now",
    description:
      "A live music chart where releases rank by the Heat behind them. Discover new music, or get your track on the board and pitch it to curators.",
    keywords: ["music chart", "music discovery", "promote music", "get music heard"],
    intro:
      "Streaming charts reward catalogues and playlist politics. The HOT Music board reworks the chart around backing: a release ranks because people put Heat behind it today.",
    sections: [
      {
        h2: "For artists and labels",
        body: [
          "List a track, an album or a release page. Fans can boost it, which turns a chart position into something your audience participates in rather than watches.",
          "Pair the board with Promote for playlist placement pitching and curator evaluation on top of the ranking.",
        ],
      },
      {
        h2: "For listeners",
        body: [
          "Every listing links straight to the release, so discovery ends in a play, not a dead end. Click counts show what people are actually opening.",
        ],
      },
    ],
    faq: [
      ["Singles or albums?", "Both, plus EPs, mixes and release pages."],
      ["Does this replace playlist pitching?", "No — the Promote layer handles curator pitching. The board handles public visibility."],
      ["Can a label list its artists?", "Yes, each artist or release can be its own listing."],
    ],
    related: ["artists", "djs", "creators", "events"],
  },
  {
    slug: "artists",
    board: "artists",
    h1: "HOT Artists",
    title: "HOT Artists — Live Artist Rankings & Discovery | BeHot.Now",
    description:
      "Discover the artists people are backing right now, or rank your artist profile on a live BeHot.Now board and connect it to your releases and bookings.",
    keywords: ["artist ranking", "discover artists", "promote artist", "artist leaderboard"],
    intro:
      "An artist profile on a live board is a public statement that something is happening. The HOT Artists board is where that statement is visible to fans, bookers and labels at the same time.",
    sections: [
      {
        h2: "What the board gives you",
        body: [
          "A ranked, shareable position, a public profile with all your socials in one place, and a click count that shows how much traffic the position produced.",
          "Fans can boost you, so a run up the board can be a community effort around a release or a tour.",
        ],
      },
      {
        h2: "Bookings and services",
        body: [
          "Artists offering features, session work, artwork or production can publish those as Marketplace services and take orders from the same profile.",
        ],
      },
    ],
    faq: [
      ["Musicians only?", "No — visual artists, producers and performers all fit here or in a neighbouring board."],
      ["Can I move between boards?", "Each listing sits on one board, but you can list on more than one."],
      ["Is my profile public?", "Yes, and it is indexed so people can find it from search."],
    ],
    related: ["music", "djs", "creators", "events"],
  },
  {
    slug: "djs",
    board: "djs",
    h1: "HOT DJs",
    title: "HOT DJs — Live DJ Rankings & Booking Discovery | BeHot.Now",
    description:
      "The live DJ board on BeHot.Now. Discover DJs who are getting attention right now, or rank your own profile and take bookings and mix work directly.",
    keywords: ["DJ ranking", "discover DJs", "book a DJ", "DJ leaderboard"],
    intro:
      "Bookers ask the same question every week: who is hot right now? The HOT DJs board answers it with a live, public ranking instead of a group chat.",
    sections: [
      {
        h2: "For DJs",
        body: [
          "Rank your profile, link your mixes, and let promoters and fans boost you. Publish mixing, editing and ghost-production services in the Marketplace and take paid work from the same page.",
        ],
      },
      {
        h2: "For venues and promoters",
        body: [
          "Browse the board, check the profile, see the socials and the services, and book without an agency in the middle.",
        ],
      },
    ],
    faq: [
      ["Do I need residencies to rank?", "No. Heat decides position, not your CV."],
      ["Can venues boost a DJ?", "Yes, anyone can boost a listing."],
      ["Can I sell mixes here?", "Yes, through Marketplace services attached to your profile."],
    ],
    related: ["music", "artists", "events", "social"],
  },
  {
    slug: "brands",
    board: "brands",
    h1: "HOT Brands",
    title: "HOT Brands — Live Brand Rankings & Visibility | BeHot.Now",
    description:
      "Where brands compete for attention in public. Discover the brands people are backing right now or put your own brand on a live BeHot.Now board.",
    keywords: ["brand ranking", "brand visibility", "promote brand", "brand leaderboard"],
    intro:
      "Brand marketing usually disappears into an ad account. A board position is the opposite: a public, permanent-looking placement that anyone can see and share.",
    sections: [
      {
        h2: "Why brands use a board",
        body: [
          "Fixed, visible pricing. A position you can point at in a report. A click count that shows exactly what the placement produced.",
          "It also works as a testing ground — you can see how a brand message performs against direct competitors on the same page.",
        ],
      },
      {
        h2: "Beyond the position",
        body: [
          "Brands can hire creators and influencers through the Marketplace and run campaigns through the Promote layer, so the board is an entry point into the whole stack rather than a standalone ad buy.",
        ],
      },
    ],
    faq: [
      ["Is this an ad network?", "No. It is a public leaderboard with public pricing and public performance."],
      ["Can agencies list on behalf of clients?", "Yes, from a professional account."],
      ["Can we hire creators here?", "Yes, through the Marketplace."],
    ],
    related: ["business", "products", "influencers", "attention"],
  },
  {
    slug: "business",
    board: "business",
    h1: "HOT Businesses",
    title: "HOT Businesses — Live Business Rankings & Discovery | BeHot.Now",
    description:
      "Discover businesses getting attention right now, or put your business on a live BeHot.Now board with public rankings, click counts and a public profile.",
    keywords: ["business ranking", "discover businesses", "small business visibility", "promote business"],
    intro:
      "Small businesses fight for visibility against companies with a hundred times the ad budget. A board levels the page: everyone is in the same list, ordered by the same rule.",
    sections: [
      {
        h2: "Visibility without an ad team",
        body: [
          "No campaign structure, no creative testing, no bidding strategy. You list, you boost, you appear.",
          "Your listing carries your real destination link, so the traffic lands on your booking page, your store or your site.",
        ],
      },
      {
        h2: "Built for repeat use",
        body: [
          "Boost around a season, a launch or a promotion, then stop. There is nothing to cancel and no monthly commitment.",
        ],
      },
    ],
    faq: [
      ["Local or online businesses?", "Both. Local businesses may also fit the Local boards."],
      ["Do I need a company account?", "A professional account unlocks services and business features, but any account can list."],
      ["Do you invoice?", "Yes, every top-up and payment generates a sequential PDF invoice in AED."],
    ],
    related: ["brands", "products", "startups", "events"],
  },
  {
    slug: "events",
    board: "events",
    h1: "HOT Events",
    title: "HOT Events — Live Event Discovery & Promotion | BeHot.Now",
    description:
      "Promote an event or discover what is coming up. The BeHot.Now events board ranks events by the Heat behind them, with public click counts on every listing.",
    keywords: ["event discovery", "promote event", "event leaderboard", "sell event tickets"],
    intro:
      "Events are the most time-sensitive thing you can promote, and the most expensive to advertise badly. A board position is bought for a fixed amount and visible immediately.",
    sections: [
      {
        h2: "For promoters",
        body: [
          "List the event with a link straight to tickets. Boost harder as the date approaches, and let attendees boost it too — a sold-out crowd pushing an event up the board is free promotion.",
        ],
      },
      {
        h2: "For people looking for something to do",
        body: [
          "The board shows what is being pushed right now, with a direct link to tickets or the venue page.",
        ],
      },
    ],
    faq: [
      ["What happens after the event?", "Boosts expire on the board's schedule, so past events drop off naturally."],
      ["Can venues list?", "Yes, venues and clubs have their own boards too."],
      ["Can I link straight to ticketing?", "Yes, your listing link can point anywhere."],
    ],
    related: ["djs", "music", "business", "brands"],
  },
  {
    slug: "gaming",
    board: "gaming",
    h1: "HOT Gaming",
    title: "HOT Gaming — Live Game Leaderboard & Discovery | BeHot.Now",
    description:
      "Discover games, studios and gaming creators getting attention right now, or put your game on a live BeHot.Now leaderboard with public rankings.",
    keywords: ["gaming leaderboard", "gaming discovery", "promote game", "indie game visibility"],
    intro:
      "Indie games live or die on visibility in the first week. The HOT Gaming board gives you a place to buy that visibility outright, with your community able to push you further.",
    sections: [
      {
        h2: "For studios",
        body: [
          "List the game, link to the store page or the demo, and boost around beta, launch and updates. Players can boost you, which turns the board into a community rally.",
        ],
      },
      {
        h2: "For players and streamers",
        body: [
          "The board surfaces what people are actively backing, and click counts show which listings are pulling real interest.",
        ],
      },
    ],
    faq: [
      ["PC, console or mobile?", "All of them, plus web games and demos."],
      ["Can streamers rank here?", "Streamers fit the Social and Creator boards, and can also list a channel."],
      ["Is there a launch window requirement?", "No, you can list at any stage."],
    ],
    related: ["apps", "products", "social", "creators"],
  },
  {
    slug: "social",
    board: "social",
    h1: "HOT Social",
    title: "HOT Social — Live Social Creator Rankings | BeHot.Now",
    description:
      "Rank a social account on a live public board. Discover the Instagram, TikTok, YouTube, X and Twitch accounts people are backing right now on BeHot.Now.",
    keywords: ["social creator ranking", "grow social following", "promote social account", "social leaderboard"],
    intro:
      "Growing a social account means fighting an algorithm for reach you cannot buy directly. A board position is reach you can buy directly, linked straight to your profile.",
    sections: [
      {
        h2: "Ranking an account",
        body: [
          "List the account with the destination link, boost it, and let your existing audience add Heat. New viewers arrive from the board rather than from a feed that has already decided who sees you.",
        ],
      },
      {
        h2: "One profile, every platform",
        body: [
          "Your BeHot.Now profile connects all your social accounts in one indexed public page, so a single board position promotes everything you run.",
        ],
      },
    ],
    faq: [
      ["Which platforms are supported?", "Instagram, TikTok, YouTube, X, Twitch and others — you connect them on your profile."],
      ["Does this buy followers?", "No. It buys visibility. What people do next is up to your content."],
      ["Can I rank more than one account?", "Yes, each account can be its own listing."],
    ],
    related: ["creators", "influencers", "gaming", "music"],
  },
];

export const HOT_TOPIC_SLUGS = HOT_TOPICS.map((t) => t.slug);

export function getHotTopic(slug: string): HotTopic | undefined {
  return HOT_TOPICS.find((t) => t.slug === slug);
}

/** Everyday search language covered by the hub (section 7 of the SEO spec). */
export const HOT_SEARCH_QUESTIONS = [
  "How do I get my startup seen?",
  "Where can I promote my app?",
  "How can I get more people to notice my product?",
  "Where can I rank my website?",
  "Can I pay to be at the top of a public leaderboard?",
  "Where can my brand compete for visibility?",
  "What products are hot right now?",
  "What AI tools are hot right now?",
  "Where can I discover new creators?",
  "Where can I discover new startups?",
  "How do I get my music discovered?",
  "How do I promote myself online?",
  "How can a small business get more visibility?",
  "Where can I put my project in front of people?",
  "How can I make my profile stand out?",
];
